// components/team/NewsletterValidationQueue.jsx
// ============================================================================
// COMPOSANT: VALIDATION DES NEWSLETTERS PAR L'ÉQUIPE
// ============================================================================
// Interface pour valider les newsletters extraites automatiquement par IA
// Style: Artsy.com - Élégant, sobre, efficace
// ============================================================================

import React, { useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";
import UploadNewsletterModal from "./UploadNewsletterModal";
import { ARTKIV_API_URL } from "../../config/domains";
import {
  Mail,
  ExternalLink,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  MapPin,
  User,
  Plus,
  Trash2,
  Upload,
  Save,
  Sparkles
} from "lucide-react";

const NewsletterValidationQueue = () => {
  const { user } = useAuth();

  // États
  const [loading, setLoading] = useState(true);
  const [newsletters, setNewsletters] = useState([]);
  const [stats, setStats] = useState({
    pending: 0,
    processed_today: 0,
    my_processed: 0
  });
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);
  
  // ⚠️ MODIFIÉ : URL de l'API Hetzner au lieu de Supabase Functions
  const NEWSLETTER_API_URL = process.env.REACT_APP_NEWSLETTER_API_URL || ARTKIV_API_URL;

  // État modal de rejection
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // Charger les newsletters en attente
  useEffect(() => {
    if (!user?.id) return;
    loadNewsletters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const loadNewsletters = async () => {
    try {
      setLoading(true);

      // 1. Charger newsletters pending
      const { data: pending, error: pendingError } = await supabase
        .from("GalerieContributions")
        .select(
          `
          id,
          gallery_id,
          contributor_id,
          contribution_type,
          status,
          data,
          created_at,
          reviewed_by,
          reviewed_at,
          rejection_reason,
          points_awarded,
          Galeries (
            id,
            nom,
            ville,
            pays
          )
        `
        )
        .eq("contribution_type", "newsletter_auto_extraction")
        .in("status", ["pending", "analyzed"])  // ⚠️ MODIFIÉ : inclut aussi "analyzed"
        .order("created_at", { ascending: false });

      if (pendingError) throw pendingError;

      setNewsletters(pending || []);

      // 2. Charger stats
      const { data: statsData, error: statsError } = await supabase.rpc(
        "get_newsletter_validation_stats",
        {
          p_user_id: user.id
        }
      );

      if (!statsError && statsData) {
        setStats(statsData);
      }
    } catch (e) {
      console.error("❌ Erreur chargement newsletters:", e);
    } finally {
      setLoading(false);
    }
  };

  // ⚠️ NOUVELLE FONCTION : Analyser le PDF via API Hetzner (OpenAI)
  const analyzeNewsletter = async (newsletter) => {
    try {
      setAnalyzingId(newsletter.id);

      // Récupérer le token de session Supabase (pour auth côté API si nécessaire)
      const {
        data: { session }
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error("Session invalide. Reconnectez-vous.");
      }

      // Appel à l'API Python sur Hetzner
      const res = await fetch(`${NEWSLETTER_API_URL}/analyze`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ 
          contribution_id: newsletter.id 
        })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `Erreur HTTP ${res.status}`);
      }

      const result = await res.json();

      if (!result.ok) {
        throw new Error(result.error || "Erreur inconnue");
      }

      console.log("✅ Analyse terminée:", result.summary);
      
      // Recharger la liste pour voir les données mises à jour
      await loadNewsletters();

      return result;
    } catch (e) {
      console.error("❌ Analyse newsletter échouée:", e);
      alert("Impossible d'analyser le PDF.\n" + (e.message || e));
      return null;
    } finally {
      setAnalyzingId(null);
    }
  };

  // Mettre à jour une exhibition
  const updateExhibition = (newsletterId, expoIdx, field, value) => {
    setNewsletters((prev) =>
      prev.map((n) => {
        if (n.id !== newsletterId) return n;

        const exhibitions = [...(n.data.exhibitions || [])];
        exhibitions[expoIdx] = {
          ...exhibitions[expoIdx],
          [field]: value
        };

        return {
          ...n,
          data: {
            ...n.data,
            exhibitions
          }
        };
      })
    );
  };

  // Mettre à jour une foire
  const updateArtFair = (newsletterId, fairIdx, field, value) => {
    setNewsletters((prev) =>
      prev.map((n) => {
        if (n.id !== newsletterId) return n;

        const fairs = [...(n.data.art_fairs || [])];
        fairs[fairIdx] = {
          ...fairs[fairIdx],
          [field]: value
        };

        return {
          ...n,
          data: {
            ...n.data,
            art_fairs: fairs
          }
        };
      })
    );
  };

  // Mettre à jour une news artiste
  const updateArtistNews = (newsletterId, newsIdx, field, value) => {
    setNewsletters((prev) =>
      prev.map((n) => {
        if (n.id !== newsletterId) return n;

        const artist_news = [...(n.data.artist_news || [])];
        artist_news[newsIdx] = {
          ...artist_news[newsIdx],
          [field]: value
        };

        return {
          ...n,
          data: {
            ...n.data,
            artist_news
          }
        };
      })
    );
  };

  // Ajouter une exposition vide
  const addEmptyExhibition = (newsletterId) => {
    setNewsletters((prev) =>
      prev.map((n) => {
        if (n.id !== newsletterId) return n;

        const exhibitions = [...(n.data.exhibitions || [])];
        exhibitions.push({
          title: "",
          artists: [],
          start_date: "",
          end_date: "",
          location: "",
          description: "",
          confidence: 0.5
        });

        return {
          ...n,
          data: {
            ...n.data,
            exhibitions
          }
        };
      })
    );
  };

  // Supprimer une exposition
  const deleteExhibition = (newsletterId, expoIdx) => {
    setNewsletters((prev) =>
      prev.map((n) => {
        if (n.id !== newsletterId) return n;

        const exhibitions = [...(n.data.exhibitions || [])];
        exhibitions.splice(expoIdx, 1);

        return {
          ...n,
          data: {
            ...n.data,
            exhibitions
          }
        };
      })
    );
  };

  // Ajouter une foire vide
  const addEmptyArtFair = (newsletterId) => {
    setNewsletters((prev) =>
      prev.map((n) => {
        if (n.id !== newsletterId) return n;

        const art_fairs = [...(n.data.art_fairs || [])];
        art_fairs.push({
          name: "",
          location: "",
          start_date: "",
          end_date: "",
          booth_number: "",
          artists: [],
          confidence: 0.5
        });

        return {
          ...n,
          data: {
            ...n.data,
            art_fairs
          }
        };
      })
    );
  };

  // Supprimer une foire
  const deleteArtFair = (newsletterId, fairIdx) => {
    setNewsletters((prev) =>
      prev.map((n) => {
        if (n.id !== newsletterId) return n;

        const art_fairs = [...(n.data.art_fairs || [])];
        art_fairs.splice(fairIdx, 1);

        return {
          ...n,
          data: {
            ...n.data,
            art_fairs
          }
        };
      })
    );
  };

  // Ajouter une news artiste vide
  const addEmptyArtistNews = (newsletterId) => {
    setNewsletters((prev) =>
      prev.map((n) => {
        if (n.id !== newsletterId) return n;

        const artist_news = [...(n.data.artist_news || [])];
        artist_news.push({
          artist_name: "",
          news_type: "",
          description: "",
          date: "",
          confidence: 0.5
        });

        return {
          ...n,
          data: {
            ...n.data,
            artist_news
          }
        };
      })
    );
  };

  // Supprimer une news artiste
  const deleteArtistNews = (newsletterId, newsIdx) => {
    setNewsletters((prev) =>
      prev.map((n) => {
        if (n.id !== newsletterId) return n;

        const artist_news = [...(n.data.artist_news || [])];
        artist_news.splice(newsIdx, 1);

        return {
          ...n,
          data: {
            ...n.data,
            artist_news
          }
        };
      })
    );
  };

  // Sauvegarder en brouillon (sans valider)
  const saveAsDraft = async (newsletter) => {
    try {
      const { error } = await supabase
        .from("GalerieContributions")
        .update({
          data: newsletter.data,
          status: "draft"
        })
        .eq("id", newsletter.id);

      if (error) throw error;

      alert("✅ Sauvegardé en brouillon");
      await loadNewsletters();
    } catch (e) {
      console.error("❌ Erreur sauvegarde:", e);
      alert("Erreur lors de la sauvegarde");
    }
  };

  // Approuver et publier la newsletter
const approveNewsletter = async (newsletter) => {
  try {
    const toInsert = [];

    // 1. Expositions → GalerieActualites type='exposition'
    const expos = newsletter.data.exhibitions || [];
    expos.forEach((expo) => {
      toInsert.push({
        gallery_id: newsletter.gallery_id,
        type: 'exposition',
        titre: expo.title,
        artistes: expo.artists,
        date_debut: expo.start_date || null,
        date_fin: expo.end_date || null,
        description: expo.description || expo.location || null,
        source_url: newsletter.data.file_url || null,
        created_by: user.id
      });
    });

    // 2. Foires → GalerieActualites type='foire'
    const fairs = newsletter.data.art_fairs || [];
    fairs.forEach((fair) => {
      toInsert.push({
        gallery_id: newsletter.gallery_id,
        type: 'foire',
        titre: fair.name,
        artistes: fair.artists || [],
        date_debut: fair.start_date || null,
        date_fin: fair.end_date || null,
        description: fair.location,
        booth_number: fair.booth_number || null,
        source_url: newsletter.data.file_url || null,
        created_by: user.id
      });
    });

    // 3. News artistes → ActusArtistes (reste séparé car c'est différent)
    const news = newsletter.data.artist_news || [];
    if (news.length > 0) {
      const newsInserts = news.map((n) => ({
        artist_name: n.artist_name,
        news_type: n.news_type,
        description: n.description,
        news_date: n.date || null,
        created_by: user.id
      }));

      const { error: newsError } = await supabase
        .from("ActusArtistes")
        .insert(newsInserts);

      if (newsError) throw newsError;
    }

    // 4. Insérer tout dans GalerieActualites
    if (toInsert.length > 0) {
      const { error: insertError } = await supabase
        .from("GalerieActualites")
        .insert(toInsert);

      if (insertError) throw insertError;
    }

    // 5. Marquer comme validé
    const { error } = await supabase
      .from("GalerieContributions")
      .update({
        status: "approved",
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString(),
        points_awarded: 10
      })
      .eq("id", newsletter.id);

    if (error) throw error;

    alert(
      `✅ Newsletter validée !\n${expos.length} expos, ${fairs.length} foires, ${news.length} actus artistes`
    );
    await loadNewsletters();
  } catch (e) {
    console.error("❌ Erreur validation:", e);
    alert("Erreur : " + e.message);
  }
};
  // Rejeter la newsletter
  const rejectNewsletter = async (newsletterId, reason) => {
    try {
      const { error } = await supabase
        .from("GalerieContributions")
        .update({
          status: "rejected",
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          rejection_reason: reason
        })
        .eq("id", newsletterId);

      if (error) throw error;

      setRejectModal(null);
      setRejectReason("");
      alert("✅ Newsletter rejetée");
      await loadNewsletters();
    } catch (e) {
      console.error("❌ Erreur rejet:", e);
      alert("Erreur lors du rejet");
    }
  };

  // Voir le PDF
  const viewPDF = async (filePath) => {
    try {
      const { data } = supabase.storage
        .from("gallery-newsletters")
        .getPublicUrl(filePath);

      if (data?.publicUrl) {
        window.open(data.publicUrl, "_blank");
      }
    } catch (e) {
      console.error("❌ Erreur ouverture PDF:", e);
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif text-gray-900">
            Validation des newsletters
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Vérifiez et publiez les actualités extraites automatiquement
          </p>
        </div>

        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
        >
          <Upload className="w-4 h-4" />
          Importer une newsletter
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">En attente</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {stats.pending}
              </p>
            </div>
            <AlertCircle className="w-8 h-8 text-orange-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Traitées aujourd'hui</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {stats.processed_today}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Mes validations</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {stats.my_processed}
              </p>
            </div>
            <User className="w-8 h-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Liste des newsletters */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : newsletters.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
          <Mail className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">Aucune newsletter en attente</p>
        </div>
      ) : (
        <div className="space-y-6">
          {newsletters.map((newsletter) => (
            <div
              key={newsletter.id}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              {/* Header */}
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <Mail className="w-6 h-6 text-blue-600" />
                    </div>

                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {newsletter.Galeries.nom}
                      </h3>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {newsletter.Galeries.ville}, {newsletter.Galeries.pays}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(newsletter.created_at).toLocaleDateString("fr-FR")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => viewPDF(newsletter.data.file_url)}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    Voir le PDF
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Contenu */}
              <div className="p-6 space-y-6">
                {/* Message si pas encore analysé */}
                {!newsletter.data.exhibitions && !newsletter.data.art_fairs && !newsletter.data.artist_news && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-900">
                          Newsletter non analysée
                        </p>
                        <p className="text-sm text-blue-700 mt-1">
                          Cliquez sur "Analyser le PDF" pour extraire automatiquement les informations avec l'IA.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* EXPOSITIONS */}
                {(newsletter.data.exhibitions?.length > 0 || false) && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-base font-medium text-gray-900">
                        Expositions ({newsletter.data.exhibitions?.length || 0})
                      </h4>
                      <button
                        onClick={() => addEmptyExhibition(newsletter.id)}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                      >
                        <Plus className="w-4 h-4" />
                        Ajouter
                      </button>
                    </div>

                    <div className="space-y-3">
                      {(newsletter.data.exhibitions || []).map((expo, expoIdx) => (
                        <div
                          key={expoIdx}
                          className="border border-gray-200 rounded-lg p-4"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <span className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                              Confiance: {Math.round((expo.confidence || 0.5) * 100)}%
                            </span>
                            <button
                              onClick={() => deleteExhibition(newsletter.id, expoIdx)}
                              className="text-gray-400 hover:text-red-500"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-500 mb-1">
                                Titre de l'exposition
                              </label>
                              <input
                                value={expo.title || ""}
                                onChange={(e) =>
                                  updateExhibition(
                                    newsletter.id,
                                    expoIdx,
                                    "title",
                                    e.target.value
                                  )
                                }
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                                placeholder="Nom de l'exposition"
                              />
                            </div>

                            <div className="col-span-2">
                              <label className="block text-xs text-gray-500 mb-1">
                                Artistes (séparés par des virgules)
                              </label>
                              <input
                                value={Array.isArray(expo.artists) ? expo.artists.join(", ") : expo.artists || ""}
                                onChange={(e) =>
                                  updateExhibition(
                                    newsletter.id,
                                    expoIdx,
                                    "artists",
                                    e.target.value.split(",").map(a => a.trim()).filter(Boolean)
                                  )
                                }
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                                placeholder="Artiste 1, Artiste 2"
                              />
                            </div>

                            <div>
                              <label className="block text-xs text-gray-500 mb-1">
                                Date début
                              </label>
                              <input
                                type="date"
                                value={expo.start_date || ""}
                                onChange={(e) =>
                                  updateExhibition(
                                    newsletter.id,
                                    expoIdx,
                                    "start_date",
                                    e.target.value
                                  )
                                }
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                              />
                            </div>

                            <div>
                              <label className="block text-xs text-gray-500 mb-1">
                                Date fin
                              </label>
                              <input
                                type="date"
                                value={expo.end_date || ""}
                                onChange={(e) =>
                                  updateExhibition(
                                    newsletter.id,
                                    expoIdx,
                                    "end_date",
                                    e.target.value
                                  )
                                }
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                              />
                            </div>

                            <div className="col-span-2">
                              <label className="block text-xs text-gray-500 mb-1">
                                Lieu
                              </label>
                              <input
                                value={expo.location || ""}
                                onChange={(e) =>
                                  updateExhibition(
                                    newsletter.id,
                                    expoIdx,
                                    "location",
                                    e.target.value
                                  )
                                }
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                                placeholder="Galerie, Ville"
                              />
                            </div>

                            <div className="col-span-2">
                              <label className="block text-xs text-gray-500 mb-1">
                                Description
                              </label>
                              <textarea
                                value={expo.description || ""}
                                onChange={(e) =>
                                  updateExhibition(
                                    newsletter.id,
                                    expoIdx,
                                    "description",
                                    e.target.value
                                  )
                                }
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                                rows={2}
                                placeholder="Description de l'exposition"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* FOIRES D'ART */}
                {(newsletter.data.art_fairs?.length > 0 || false) && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-base font-medium text-gray-900">
                        Foires d'art ({newsletter.data.art_fairs?.length || 0})
                      </h4>
                      <button
                        onClick={() => addEmptyArtFair(newsletter.id)}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                      >
                        <Plus className="w-4 h-4" />
                        Ajouter
                      </button>
                    </div>

                    <div className="space-y-3">
                      {(newsletter.data.art_fairs || []).map((fair, fairIdx) => (
                        <div
                          key={fairIdx}
                          className="border border-gray-200 rounded-lg p-4"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <span className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                              Confiance: {Math.round((fair.confidence || 0.5) * 100)}%
                            </span>
                            <button
                              onClick={() => deleteArtFair(newsletter.id, fairIdx)}
                              className="text-gray-400 hover:text-red-500"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-500 mb-1">
                                Nom de la foire
                              </label>
                              <input
                                value={fair.name || ""}
                                onChange={(e) =>
                                  updateArtFair(newsletter.id, fairIdx, "name", e.target.value)
                                }
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                                placeholder="Art Basel Miami"
                              />
                            </div>

                            <div className="col-span-2">
                              <label className="block text-xs text-gray-500 mb-1">
                                Lieu
                              </label>
                              <input
                                value={fair.location || ""}
                                onChange={(e) =>
                                  updateArtFair(newsletter.id, fairIdx, "location", e.target.value)
                                }
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                                placeholder="Miami Beach Convention Center"
                              />
                            </div>

                            <div>
                              <label className="block text-xs text-gray-500 mb-1">
                                Date début
                              </label>
                              <input
                                type="date"
                                value={fair.start_date || ""}
                                onChange={(e) =>
                                  updateArtFair(newsletter.id, fairIdx, "start_date", e.target.value)
                                }
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                              />
                            </div>

                            <div>
                              <label className="block text-xs text-gray-500 mb-1">
                                Date fin
                              </label>
                              <input
                                type="date"
                                value={fair.end_date || ""}
                                onChange={(e) =>
                                  updateArtFair(newsletter.id, fairIdx, "end_date", e.target.value)
                                }
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                              />
                            </div>

                            <div>
                              <label className="block text-xs text-gray-500 mb-1">
                                Numéro de stand
                              </label>
                              <input
                                value={fair.booth_number || ""}
                                onChange={(e) =>
                                  updateArtFair(newsletter.id, fairIdx, "booth_number", e.target.value)
                                }
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                                placeholder="A12"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* NEWS ARTISTES */}
                {(newsletter.data.artist_news?.length > 0 || false) && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-base font-medium text-gray-900">
                        Actualités artistes ({newsletter.data.artist_news?.length || 0})
                      </h4>
                      <button
                        onClick={() => addEmptyArtistNews(newsletter.id)}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                      >
                        <Plus className="w-4 h-4" />
                        Ajouter
                      </button>
                    </div>

                    <div className="space-y-3">
                      {(newsletter.data.artist_news || []).map((news, newsIdx) => (
                        <div
                          key={newsIdx}
                          className="border border-gray-200 rounded-lg p-4"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <span className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                              Confiance: {Math.round((news.confidence || 0.5) * 100)}%
                            </span>
                            <button
                              onClick={() => deleteArtistNews(newsletter.id, newsIdx)}
                              className="text-gray-400 hover:text-red-500"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">
                                Artiste
                              </label>
                              <input
                                value={news.artist_name || ""}
                                onChange={(e) =>
                                  updateArtistNews(
                                    newsletter.id,
                                    newsIdx,
                                    "artist_name",
                                    e.target.value
                                  )
                                }
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                                placeholder="Nom de l'artiste"
                              />
                            </div>

                            <div>
                              <label className="block text-xs text-gray-500 mb-1">
                                Type
                              </label>
                              <input
                                value={news.news_type || ""}
                                onChange={(e) =>
                                  updateArtistNews(
                                    newsletter.id,
                                    newsIdx,
                                    "news_type",
                                    e.target.value
                                  )
                                }
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                                placeholder="Prix, Musée, Publication..."
                              />
                            </div>

                            <div className="col-span-2">
                              <label className="block text-xs text-gray-500 mb-1">
                                Description
                              </label>
                              <textarea
                                value={news.description || ""}
                                onChange={(e) =>
                                  updateArtistNews(
                                    newsletter.id,
                                    newsIdx,
                                    "description",
                                    e.target.value
                                  )
                                }
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                                rows={2}
                                placeholder="Détails"
                              />
                            </div>

                            <div>
                              <label className="block text-xs text-gray-500 mb-1">
                                Date
                              </label>
                              <input
                                value={news.date || ""}
                                onChange={(e) =>
                                  updateArtistNews(newsletter.id, newsIdx, "date", e.target.value)
                                }
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                                placeholder="YYYY-MM-DD"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-6 border-t border-gray-100">
                  <div className="flex gap-3">
                    <button
                      onClick={() => analyzeNewsletter(newsletter)}
                      disabled={analyzingId === newsletter.id}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-colors font-medium ${
                        analyzingId === newsletter.id
                          ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                          : "bg-blue-600 text-white hover:bg-blue-700"
                      }`}
                    >
                      <Sparkles className="w-4 h-4" />
                      {analyzingId === newsletter.id ? "Analyse en cours…" : "Analyser le PDF"}
                    </button>

                    <button
                      onClick={() => approveNewsletter(newsletter)}
                      className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Valider et Publier
                    </button>

                    <button
                      onClick={() => saveAsDraft(newsletter)}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      <Save className="w-4 h-4" />
                      Sauvegarder
                    </button>
                  </div>

                  <button
                    onClick={() => setRejectModal(newsletter.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <XCircle className="w-4 h-4" />
                    Rejeter
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Rejection */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Rejeter la newsletter
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Indiquez une raison (optionnel).
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              rows={3}
              placeholder="Ex: PDF illisible, hors périmètre, doublon…"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setRejectModal(null);
                  setRejectReason("");
                }}
                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={() => rejectNewsletter(rejectModal, rejectReason)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Rejeter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload modal */}
      <UploadNewsletterModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploaded={() => {
          setShowUploadModal(false);
          loadNewsletters();
        }}
      />
    </div>
  );
};

export default NewsletterValidationQueue;